import React from 'react';
import { Col, Row, Card, Badge, ProgressBar } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import './assessment.css';
import './learning-resources.css';
import { NoDataFound } from './CommonComponents';
import './common-components.css';

function SubjectAssessments(props) {
  const { assessmentData, isLoading = false } = props

  const location = useLocation();
  const history = useHistory()

  const query = new URLSearchParams(location.search);
  const subjectID = query.get('subjectID');
  const currentPath = location.pathname;

  // Beautiful color palette for assessment cards
  const assessmentColors = [
    { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', light: '#f0ebff' },
    { bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', light: '#e8fff5' },
    { bg: 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)', light: '#fff0f3' },
    { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', light: '#fff0f8' },
    { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', light: '#e8f7ff' },
    { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', light: '#fff8e8' },
  ];

  // 🔐 Lock logic (safe date comparison)
  const isExamLocked = (startDate) => {
    if (!startDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const examDate = new Date(startDate);
    examDate.setHours(0, 0, 0, 0);

    return examDate > today;
  };

  return (
    <Col xl="12" className="mb-5">
      {/* Assessment Cards Grid */}
      <Row className="g-4 assessment-grid">
        {assessmentData?.map((assessment, i) => {
          const locked = isExamLocked(assessment.assessmentStartDate);
          const colorSet = assessmentColors[i % assessmentColors.length];
          const passPercentage = assessment.total_marks ? Math.round((assessment.pass_marks / assessment.total_marks) * 100) : 0;

          return (
            <Col
              xs="12"
              sm="6"
              lg="4"
              xl="3"
              key={i}
              className={`mb-3 ${locked ? "locked-assessment" : ""}`}
              onClick={() => {
                if (locked) return;

                if (assessment.isCompleted === "True") {
                  history.push(`/viewResults?assessment_id=${assessment.id}`);
                } else {
                  history.push(`/subject/assessment?assessmentId=${assessment.id}&duration=${assessment.assessmentDuration ? assessment.assessmentDuration : 30}`);
                }
              }}
            >
              <Card className={`assessment-card h-100 ${locked ? 'locked' : ''} ${assessment.isCompleted === "True" ? 'completed' : ''}`}>
                {/* Card Header with Gradient */}
                <div 
                  className="assessment-card-header"
                  style={{ background: locked ? '#ccc' : colorSet.bg }}
                >
                  <div className="assessment-icon">
                    {locked ? (
                      <i className="bi bi-lock-fill"></i>
                    ) : assessment.isCompleted === "True" ? (
                      <i className="bi bi-check-circle-fill"></i>
                    ) : (
                      <i className="bi bi-pencil-square"></i>
                    )}
                  </div>
                  <div className="assessment-number">
                    Test {String(i + 1).padStart(2, '0')}
                  </div>
                  {assessment.isCompleted === "True" && (
                    <div className="completed-badge">
                      <i className="bi bi-check-lg"></i> Completed
                    </div>
                  )}
                </div>

                <Card.Body className="assessment-card-body">
                  {/* Assessment Title */}
                  <h5 className="assessment-title">
                    {assessment.assessment}
                  </h5>

                  {/* Score Display for Completed Assessment */}
                  {assessment.isCompleted === "True" && (
                    <div className="completed-score-section">
                      <div className="score-display-card">
                        <div className="score-item-display">
                          <i className="bi bi-trophy-fill"></i>
                          <span className="score-value-display">{assessment.total_score || 0}/{assessment.total_marks}</span>
                          <span className="score-label-display">Score</span>
                        </div>
                        <div className="points-item-display">
                          <i className="bi bi-star-fill"></i>
                          <span className="points-value-display">{(assessment.total_score || 0) * 10}</span>
                          <span className="points-label-display">Points</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Assessment Stats */}
                  <div className="assessment-stats">
                    <div className="stat-item">
                      <div className="stat-icon">
                        <i className="bi bi-question-circle"></i>
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{assessment.no_of_questions}</span>
                        <span className="stat-label">Questions</span>
                      </div>
                    </div>

                    <div className="stat-item">
                      <div className="stat-icon">
                        <i className="bi bi-star"></i>
                      </div>
                      <div className="stat-content">
                        <span className="stat-value">{assessment.total_marks}</span>
                        <span className="stat-label">Total Marks</span>
                      </div>
                    </div>

                    {assessment.assessmentDuration && (
                      <div className="stat-item">
                        <div className="stat-icon">
                          <i className="bi bi-clock"></i>
                        </div>
                        <div className="stat-content">
                          <span className="stat-value">{assessment.assessmentDuration}</span>
                          <span className="stat-label">Minutes</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pass Marks Progress */}
                  <div className="pass-marks-section">
                    <div className="pass-marks-label">
                      <span>Pass Marks: {assessment.pass_marks}</span>
                      <span>{passPercentage}%</span>
                    </div>
                    <ProgressBar 
                      now={passPercentage} 
                      className="pass-marks-progress"
                      variant={passPercentage >= 70 ? 'danger' : passPercentage >= 50 ? 'warning' : 'success'}
                    />
                  </div>

                  {/* Locked Message */}
                  {locked && (
                    <div className="locked-message">
                      <i className="bi bi-calendar-event me-2"></i>
                      Opens on {new Date(assessment.assessmentStartDate).toLocaleDateString()}
                    </div>
                  )}

                  {/* Action Button */}
                  <div className={`assessment-action ${locked ? 'locked' : ''}`}>
                    {locked ? (
                      <span><i className="bi bi-lock me-2"></i>Locked</span>
                    ) : assessment.isCompleted === "True" ? (
                      <span>View Results <i className="bi bi-arrow-right ms-2"></i></span>
                    ) : (
                      <span>Start Test <i className="bi bi-arrow-right ms-2"></i></span>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Empty State */}
      {(!assessmentData || assessmentData.length === 0) && (
        <NoDataFound
          title="No Practice Tests Available"
          message="Practice assessments will appear here once they are created for this lesson."
          icon="pencil-square"
          showBackButton={false}
        />
      )}
    </Col>
  );
}

export default SubjectAssessments;
